import { useLayoutEffect, useRef, useState } from 'react';
import Collapsible from 'react-collapsible';
import Navbar from '../../components/Navbar/Navbar';
import HeaderImage from '../../../images/charco-security.svg';
import ArrowUp from '../../../images/arrow-up.svg';
import ArrowDown from '../../../images/arrow-down.svg';
import ScrollTopIcon from '../../../images/arrow-up-circle.svg';
import * as Styled from './LegalStyles';
import {useLocation} from 'react-router-dom';
import FooterNav from '../../components/FooterNav/FooterNav';
import Meta from '../../components/Meta/Meta';


const PrivacyPolicy = () => {
  const location = useLocation();
  const [shieldOpen, setShieldOpen] = useState(false);
  const pageRef = useRef(null);
  const navLinks = [{
    title: 'Privacy Policy',
    link: '/privacy'
  }, {
    title: 'Terms of Service',
    link: '/tos'
  }, {
    title: 'Security Practices',
    link: '/security'
  }]
  const toggleShieldOpen = () => {
    setShieldOpen(!shieldOpen);
  }
  useLayoutEffect(() => {
    if (!location.hash) {
      return;
    }
    if (pageRef && pageRef.current) {
      // @ts-ignore
      const el = pageRef.current.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }
  }, [location.hash]);
  return  (
    <section>
      <Meta page="privacy" />
      <Navbar
        title="Privacy & Terms"
        links={navLinks}
      />
      <Styled.PageWrapper ref={pageRef}>
        <a href="#top" className="ScrollTopButton">
          <Styled.ScrollTopIcon src={ScrollTopIcon} />
        </a>
        <Styled.H1 id="top">
          Privacy Policy
        </Styled.H1>
        <Styled.Heading>
          <Styled.Img src={HeaderImage} alt="privacy policy" />
          <Styled.H4>
            Trova is committed to create a secure space for our users and your privacy matters to us.            </Styled.H4>
          <Styled.Subtitle>
            Effective date, June 1, 2021
          </Styled.Subtitle>
        </Styled.Heading>
        <Styled.H3>
          What you need to know in a few words
        </Styled.H3>
        <Styled.Paragraph>
          Trova understand that by choosing Trova AI, Inc, you are placing some of your important data in our hands. We are only in business if you trust us, so your privacy means everything to us! Here are the things we want you to know:
          Trova keep your name, email, and company so you can use our system. We place cookies on your browser to make our system faster. We may use your name and email to send you information about our system. Please keep your personal information up to date. <Styled.Font600>We do not and will not sell Your Data, individually or in aggregated form.</Styled.Font600> You may stop receiving information from us at any time. You may access your personal data at any time. You are responsible for maintaining the privacy of data you enter into our system. We keep your data very private on our cloud providers. At present, we use Amazon Web Services (AWS). Trova AI, Inc is a U.S. company. We follow the laws of all the countries where we operate, and follow GDPR requirements.
        </Styled.Paragraph>
          
        <Styled.Paragraph>
          Trova's use and transfer to any other app of information received from Google APIs will adhere to <a href="https://developers.google.com/terms/api-services-user-data-policy">Google API Services User Data Policy</a> including the Limited Use requirements.
        </Styled.Paragraph>
        <Styled.Paragraph>
           If you need more information, please see the sections below.
        </Styled.Paragraph>
        <Styled.H3>
          Table of contents
        </Styled.H3>
        <Styled.PageLink to="#about">
          About This Privacy Policy
        </Styled.PageLink>
        <Styled.PageLink to="#information">
          Information We Collect and Receive
        </Styled.PageLink>
        <Styled.PageLink to="#howweuseinformation">
          How We Use the Information We Collect
        </Styled.PageLink>
        <Styled.PageLink to="#howweshareinformation">
          How We Share and Disclose Information
        </Styled.PageLink>
        <Styled.PageLink to="#disclosure">
          Other Access to or Disclosure of Your Information
        </Styled.PageLink>
        <Styled.PageLink to="#security">
          Security and Information Protection
        </Styled.PageLink>
        <Styled.PageLink to="#controls">
          Your Controls and Choices
        </Styled.PageLink>
        <Styled.PageLink to="#changes">
          Changes to Privacy Policy
        </Styled.PageLink>
        <Styled.PageLink to="#international">
          International Transfer of Data
        </Styled.PageLink>
        <Styled.PageLink to="#contact">
          Contact Us
        </Styled.PageLink>
        <Styled.PageLink to="#subprocessors">
          Trova AI Subprocessors
        </Styled.PageLink>
        <Styled.PageLink to="#privacyshield">
          Privacy Shield Notice
        </Styled.PageLink>

        <Styled.H4 id="about">
          About This Privacy Policy
        </Styled.H4>
        <Styled.Paragraph>
          This Privacy Policy (the “Policy”) applies to the website <a href="https://gotrova.ai">https://gotrova.ai</a> and various related services (collectively, the “Sites”, “Services”, “we”, “us”, or “our”) to you (“You”), the user (“User”). This Policy describes how we collect, use and disclose information, and what choices you have with respect to the information. This Policy should be read in conjunction with our <a href="/tos">Terms of Service</a>.
          We understand that by choosing Trova AI, Inc, you are placing some of your important data in our hands. We are only in business if you trust us, so your privacy means everything to us! Here are the key things you should know:
        </Styled.Paragraph>
        <Styled.H4 id="information">
          Information We Collect and Receive
        </Styled.H4>
        <Styled.Paragraph>
          Our primary goals in collecting information are to provide and improve our Services, to administer your use of the Services and to enable you to enjoy and easily navigate our Services. We collect and use this information to provide, improve and protect our Services.
        </Styled.Paragraph>
        <Styled.Paragraph style={{ fontWeight: 600 }}>
          A. Information Related to Your Account and Interaction with Trova AI, Inc.
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>Registration and Contact Information.</Styled.Font600> We collect certain “Personally Identifiable Information” from you when you sign up to our Services, that can be used to identify you, such as your name, email address, mailing address, telephone number, and any other information that we deem relevant for the purpose of providing you with our Services or which you provided to use voluntarily. This category also includes information tied to your identity that you provide us through other means, such as emails to our support service.
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>Technical, Usage and Location Information.</Styled.Font600> Whenever you visit our Site, we may collect ”Non-identifying Information” from you, such as your referring URL, browser, operating system, cookie information, and Internet Service Provider. Without a subpoena, voluntary compliance on the part of your Internet Service Provider, or additional records from a third party, this information alone cannot usually be used to identify you.
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>Other Information.</Styled.Font600> We receive additional information provided to us when submitted to our Sites or if you participate in a focus group, contest, activity or event, apply for a job, or otherwise communicate with Trova AI, Inc.
        </Styled.Paragraph>
        <Styled.Paragraph>
          Trova AI, Inc’s use of information received, and Trova AI, Inc’s transfer of information to any other app, from Google APIs will adhere to Google’s Limited Use Requirements.
        </Styled.Paragraph>
        <Styled.Paragraph style={{ fontWeight: 600 }}>
          B. Information Related to Our Service
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>Information.</Styled.Font600> In order to perform the Services, we ask for visibility into your applications, but you can revoke our access to your applications (“Your Data”) at any time. Your Data may include, without limitation, personal information such as names, email addresses and phone numbers, and other properties which information may be input into the Services by you.
        </Styled.Paragraph>
        <Styled.Paragraph style={{ fontWeight: 600 }} id="cookies">
          C. Cookies
        </Styled.Paragraph>
        <Styled.Paragraph>
          We use cookies, tracking pixels and related technologies. Cookies are small data files that are served by our platform and stored on your device. Our Site uses cookies dropped by us or third parties for a variety of purposes including to operate and personalize the Site. We use technologies like cookies and pixel tags to provide, improve, protect and promote our Services. For example, cookies help us with things like remembering your username for your next visit, understanding how you are interacting with our Services, and improving them based on that information. Cookies are small pieces of information that are stored as text files by your Internet browser on your computer’s hard drive. Most Internet browsers are initially set to accept cookies. You can set your browser to refuse cookies from websites or to remove cookies from your hard drive, but this may limit your ability to use the Services.
        </Styled.Paragraph>
        <Styled.H4 id="howweuseinformation">
          How We Use the Information We Collect
        </Styled.H4>
        <Styled.Paragraph>
          We use your information in the following ways:
          <ul>
            <li><Styled.Font600>to provide, update, maintain, improve and protect our Services,</Styled.Font600> including to operate certain features and functionality of the Services, to prevent or address service errors, security or technical issues, analyze and monitor usage, trends and other activities.</li>
            <li><Styled.Font600>to respond to your support requests, comments and questions.</Styled.Font600></li>
            <li><Styled.Font600>to control unauthorized use or abuse of the Services,</Styled.Font600> if we have a good faith belief, or have received a complaint alleging, that Your Data is in violation of our Acceptable Use Guidelines.</li>
            <li><Styled.Font600>to communicate directly with you,</Styled.Font600> including by sending you newsletters, promotions and special offers or information about new products and services. You may, at any time, request to opt-out of receiving future emails or notifications from us, by contacting us.</li>
          </ul>
        </Styled.Paragraph>
        <Styled.H4 id="howweshareinformation">
          How We Share and Disclose Information
        </Styled.H4>
        <Styled.Paragraph>
          We do not and will not sell Your Data, individually or in aggregated form. We may share or transfer your personal information to third parties only in the following limited circumstances:
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>A. Information Related to Your Account and Interaction with Trova AI, Inc.</Styled.Font600>
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>We will display your Account information</Styled.Font600> on <a href="https://app.gotrova.ai/account">https://app.gotrova.ai/account</a> profile page and elsewhere through the Services in accordance with the preferences you set in your Account. You can review and revise your Account information at any time.
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>We will display your Your Data within the Services as directed by you.</Styled.Font600> If you elect to use a third party application through the Services, then we may share or disclose Your Data with that third party application as directed by you. Please remember that we are not responsible for the privacy practices of such third parties so you should make sure you trust the application and that it has a privacy policy acceptable to you.
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>With Trusted Service Providers and Business Partners.</Styled.Font600> We may utilize trusted third party service providers to assist us in delivering, improving, protecting and promoting the Services. For example, we may use third parties to help host our Services, send out email updates, perform analyses related to the operation of the Services, or process payments. These third party service providers may have access to Your Data for the limited purpose of providing the service we have contracted with them to provide. They are required to have a privacy policy and security standards in place that are at least as protective of your information as is this Privacy Policy.
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>In Connection With a Sale or Change of Control.</Styled.Font600> If the ownership of all or substantially all of our business changes, we may transfer your information to the new owner so that the Services can continue to operate. In such case, your information would remain subject to the promises and commitments contained in this Privacy Policy until such time as this Privacy Policy is updated or amended by the acquiring party upon notice to you.
        </Styled.Paragraph>
        <Styled.H4 id="disclosure">
          Other Access to or Disclosure of Your Information
        </Styled.H4>
        <Styled.Paragraph>
          We restrict access to any data and content you transmit to Trova AI, Inc or is otherwise made available via the Services to Trova AI, Inc employees, payment processors, contractors and agents who need to know that information in order to perform the Services. All of these individuals are subjected to strict contractual confidentiality obligations and may be disciplined or terminated if they fail to meet these obligations.
        </Styled.Paragraph>
        <Styled.H4 id="security">
          Security and Information Protection
        </Styled.H4>
        <Styled.Paragraph>
          We take security seriously. We take various steps to protect information you provide to us from loss, misuse, and unauthorized access or disclosure. These steps take into account the sensitivity of the information we collect, process and store, and the current state of technology. We use commercially reasonable and industry-standard physical, managerial, and technical safeguards to preserve the integrity and security of your information. To learn more about our current practices and policies regarding data privacy, security and confidentiality, please see <a href="https://gotrova.ai/security">https://gotrova.ai/security</a> .
        </Styled.Paragraph>
        <Styled.H4 id="controls">
          Your Controls and Choices
        </Styled.H4>
        <Styled.Paragraph>
          <Styled.Font600>Opt-Outs.</Styled.Font600> Trova AI, Inc provides you with the opportunity to choose (opt out) whether your personal information is to be disclosed to a third party or to be used for a purpose that is incompatible with the purpose(s) for which it was originally collected or subsequently authorized. You may exercise your choice by contacting us at: <a href="mailto:info@gotrova.ai">info@gotrova.ai</a> If you decide to opt-out, we may not be able to provide certain features of the Services to you.
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>Communication Preferences.</Styled.Font600> If you receive commercial email from us, you may unsubscribe at any time by following the instructions contained within the email. You may also opt-out from receiving commercial e-mail from us, and any other promotional communications that we may send to you from time to time by sending your request to us by e-mail at <a href="mailto:info@gotrova.ai">info@gotrova.ai</a> or by writing to us at the address given at the end of this policy. Additionally, we may allow you to view and modify settings relating to the nature and frequency of promotional communications that you receive from us.
        </Styled.Paragraph>
        <Styled.Paragraph>
          Please be aware that if you opt-out of receiving commercial e-mail from us, it may take up to ten business days for us to process your opt-out request, and you may receive commercial e-mail from us during that period. Additionally, even after you opt-out from receiving commercial messages from us, you will continue to receive administrative messages from us regarding our Service.
        </Styled.Paragraph>
        <Styled.Paragraph>
          <Styled.Font600>Blocking Cookies.</Styled.Font600> You can remove or block certain cookies using the settings in your browser but the Offerings may cease to function properly if you do so. You may also change or withdraw your cookie consent from this Privacy Policy – <a href="#cookies">Cookies</a> section.
        </Styled.Paragraph>
        <Styled.H4 id="changes">
          Changes to Privacy Policy
        </Styled.H4>
        <Styled.Paragraph>
          We reserve the right to change our Privacy Policy at any time. If we make changes, we will post them and will indicate on this page the policy’s new effective date. If we make material changes to this policy, we will notify you by email or through notice on the Services.
        </Styled.Paragraph>
        <Styled.H4 id="international">
          International Transfer of Data
        </Styled.H4>
        <Styled.Paragraph>
          We may transfer your personal information to countries other than the one in which you live. We will protect your personal information in accordance with this Privacy Policy wherever it is processed. In addition, we deploy the following safeguards if we transfer personal information originating from the European Union or Switzerland to other countries not deemed adequate under applicable data protection law:
        </Styled.Paragraph>
        <Styled.Paragraph>
          E.U.-U.S. Privacy Shield and Swiss-U.S. Privacy Shield. To comply with European Union and Swiss data protection laws, Trova AI, Inc self-certified under the E.U.-U.S. Privacy Shield and the Swiss-U.S. Privacy Shield. These frameworks were developed to enable companies to comply with data protection requirements when transferring personal data from the European Union and Switzerland to the United States. For more information, including the types of Information covered, see Trova AI, Inc’s EU-U.S. and Swiss-U.S. Privacy Shield Policy. To learn more about the Privacy Shield Program, please see <a href="https://www.privacyshield.gov/welcome">https://www.privacyshield.gov/welcome</a>.
        </Styled.Paragraph>
        <Styled.H4 id="contact">
          Contact Us
        </Styled.H4>
        <Styled.Paragraph>
          If you have questions or need to contact us about these Terms or the Site, please email us at <a href="mailto:info@gotrova.ai">info@gotrova.ai</a>. You can reach Trova AI, Inc by mail at:
        </Styled.Paragraph>
        <Styled.Paragraph>
          Trova AI, Inc
        </Styled.Paragraph>
        <Styled.Paragraph>
          60 Hazelwood Dr,
        </Styled.Paragraph>
        <Styled.Paragraph>
          Champaign, Illinois, 61820
        </Styled.Paragraph>
        <Styled.H4 id="subprocessors">
          Trova AI Subprocessors
        </Styled.H4>
        <Styled.Paragraph>
          <Styled.Font600>Security, Privacy and Compliance Information</Styled.Font600>
        </Styled.Paragraph>
        <Styled.Paragraph>
          Trova AI leverages third party subprocessors that help with delivery of our Services. Prior to engaging any third party subprocessor, Trova AI performs diligence to evaluate their privacy, security and confidentiality practices, and executes an agreement implementing its applicable obligations.
        </Styled.Paragraph>
        <Styled.ListsContainer>
          <Styled.List>
            <Styled.ListItem className="ListTitle">
              Entity Name
            </Styled.ListItem>
            <Styled.ListItem>
              Amazon Web Services, Inc
            </Styled.ListItem>
            <Styled.ListItem>
              Google, Inc
            </Styled.ListItem>
            <Styled.ListItem>
              Mongo DB, Inc
            </Styled.ListItem>
            <Styled.ListItem>
              Atlassian
            </Styled.ListItem>
          </Styled.List>
          <Styled.List className="ml-5">
            <Styled.ListItem className="ListTitle">
              Purposes
            </Styled.ListItem>
            <Styled.ListItem>
              Cloud Service Provider
            </Styled.ListItem>
            <Styled.ListItem>
              Cloud Service Provider
            </Styled.ListItem>
            <Styled.ListItem>
              Database Hosting
            </Styled.ListItem>
            <Styled.ListItem>
              Infrastructure Monitoring
            </Styled.ListItem>
          </Styled.List>
        </Styled.ListsContainer>
        <Collapsible
          trigger={
            <Styled.H4 id="privacyshield" style={{cursor: 'pointer'}}>
              Privacy Shield Notice
              <Styled.Icon
                src={shieldOpen ? ArrowUp : ArrowDown} alt="collapse privacy shield"
                style={{ marginLeft: 40 }}
              />
            </Styled.H4>
          }
          onOpen={toggleShieldOpen}
          onClose={toggleShieldOpen}
        >
          <Styled.Paragraph>
            <Styled.Font600>For Data Transfer from the EU and Switzerland into the United States: EU-US Privacy Shield Framework and Swiss-US Privacy Shield Framework Notice</Styled.Font600>
          </Styled.Paragraph>
          <Styled.Paragraph>
            Trova AI, Inc, inc complies with the EU-US Privacy Shield Framework and the Swiss-US Privacy Shield Framework as set forth by the US Department of Commerce regarding the collection, use, and retention of personal information from European Union member countries and Switzerland transferred to the United States pursuant to Privacy Shield. Trova AI, Inc has certified that it adheres to the Privacy Shield Principles with respect to such data. If there is any conflict between the policies in this privacy policy and data subject rights under the Privacy Shield Principles, the Privacy Shield Principles shall govern. To learn more about the Privacy Shield program, please visit <a href="https://www.privacyshield.gov/">https://www.privacyshield.gov/</a>, and to view our certification page, please visit here.
          </Styled.Paragraph>
          <Styled.Paragraph>
            In particular, if Trova AI, Inc transfers to its service providers any personal information that Trova AI, Inc received in reliance on its Privacy Shield certification, Trova AI, Inc is responsible for the third parties’ processing of that personal information, unless Trova AI, Inc proves that it is not responsible for the event giving rise to the damage.
          </Styled.Paragraph>
          <Styled.Paragraph>
            With respect to the Personal Information received or transferred pursuant to the Privacy Shield Framework, Trova AI, Inc is subject to the regulatory enforcement powers of the U.S. Federal Trade Commission. In certain situations, Trova AI, Inc may be required to disclose Personal Information in response to lawful requests by public authorities, including to meet national security or law enforcement requirements.
          </Styled.Paragraph>
          <Styled.Paragraph>
            Trova AI, Inc acknowledges the right of EU and Swiss individuals to access their personal data pursuant to the Privacy Shield. Individuals wishing to exercise this right may do so by contacting us at <a href="mailto:info@gotrova.ai">info@gotrova.ai</a>
          </Styled.Paragraph>
          <Styled.Paragraph>
            <Styled.Font600>Contacting Us, Complaints and Dispute Resolution</Styled.Font600>
          </Styled.Paragraph>
          <Styled.Paragraph>
            In compliance with the Privacy Shield Principles, Trova AI, Inc, inc commits to resolve complaints about your privacy and our collection or use of your personal information transferred to the United States pursuant to Privacy Shield. European Union and Swiss individuals with Privacy Shield inquiries or complaints should first contact us email at <a href="mailto:info@gotrova.ai">info@gotrova.ai</a> We will work to resolve your issue and will respond within 45 days of receipt.
          </Styled.Paragraph>
          <Styled.Paragraph>
            Trova AI, Inc has further committed to refer unresolved privacy complaints under the Privacy Shield Principles to an independent dispute resolution mechanism, the BBB EU PRIVACY SHIELD, operated by the Council of Better Business Bureaus. If you do not receive timely acknowledgment of your complaint, or if your complaint is not satisfactorily addressed, please visit <a href="www.bbb.org/EU-privacy-shield/for-eu-consumers">www.bbb.org/EU-privacy-shield/for-eu-consumers</a> for more information and to file a complaint. This service is provided free of charge to you.
          </Styled.Paragraph>
          <Styled.Paragraph>
            If your Privacy Shield complaint cannot be resolved through the above channels, under certain conditions, you may invoke binding arbitration for some residual claims not resolved by other redress mechanisms. See Privacy Shield Annex 1 at <a href="https://www.privacyshield.gov/article?id=ANNEX-I-introduction">https://www.privacyshield.gov/article?id=ANNEX-I-introduction</a>.
          </Styled.Paragraph>
        </Collapsible>
      </Styled.PageWrapper>
      <FooterNav />
    </section>
  )
}

export default PrivacyPolicy;